import { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { X, Camera } from "lucide-react";
import { LostItem } from "@/types";
import { useImageUpload } from '@/hooks/useImageUpload';
import { supabase } from '@/integrations/supabase/client';

const LOCATION_OPTIONS = [
  { value: "Pool", label: "Pool" },
  { value: "Clubhouse", label: "Clubhouse" },
  { value: "Street", label: "Street" },
  { value: "Barbecue", label: "Barbecue" },
];

interface EditLostItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: LostItem | null;
  onSave: (values: { description: string; location: string; found_by: string; photo: string | null }) => void;
}

const EditLostItemModal = ({ open, onOpenChange, item, onSave }: EditLostItemModalProps) => {
  const [description, setDescription] = useState(item?.description || "");
  const [location, setLocation] = useState((item as any)?.location || "");
  const [foundBy, setFoundBy] = useState((item as any)?.found_by || "");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(item?.photo || null);
  const { uploadImage, isUploading } = useImageUpload();
  const [signedPhotoUrl, setSignedPhotoUrl] = useState<string | null>(null);
  const fileReaderRef = useRef<FileReader | null>(null);

  useEffect(() => {
    setDescription(item?.description || "");
    setLocation((item as any)?.location || "");
    setFoundBy((item as any)?.found_by || "");
    setPhotoPreview(item?.photo || null);
    setPhotoFile(null);
  }, [item, open]);

  useEffect(() => {
    let isMounted = true;
    async function fetchSignedUrl() {
      if (photoPreview && !photoPreview.startsWith('data:')) {
        const { data, error } = await supabase.storage.from('lost-and-found-photos').createSignedUrl(photoPreview, 60 * 60);
        if (isMounted && data?.signedUrl) setSignedPhotoUrl(data.signedUrl);
      } else {
        setSignedPhotoUrl(null);
      }
    }
    fetchSignedUrl();
    return () => { isMounted = false; };
  }, [photoPreview]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (fileReaderRef.current) {
      fileReaderRef.current.abort();
      fileReaderRef.current = null;
    }
  }, []);

  // Cleanup on modal close
  useEffect(() => {
    if (!open) {
      cleanup();
    }
  }, [open, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhotoFile(file);
      // Cleanup previous FileReader
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
      }
      // Create a preview URL
      const reader = new FileReader();
      fileReaderRef.current = reader;
      reader.onloadend = () => {
        if (fileReaderRef.current === reader) {
          setPhotoPreview(reader.result as string);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!item) return;
    let finalPhotoUrl = photoPreview;
    // Only upload if photoPreview is a base64 string
    if (photoPreview && photoPreview.startsWith('data:')) {
      finalPhotoUrl = await uploadImage(photoPreview, String(item.id));
    } else if (photoPreview && !photoPreview.startsWith('data:')) {
      finalPhotoUrl = photoPreview;
    }
    console.log('[EditLostItemModal] handleSave values:', {
      description,
      location,
      foundBy,
      finalPhotoUrl,
    });
    onSave({
      description,
      location,
      found_by: foundBy,
      photo: finalPhotoUrl,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Lost Item</DialogTitle>
          <DialogClose className="absolute right-4 top-4">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea
              className="w-full min-h-[100px] px-3 py-2 text-sm border rounded-md"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Location</label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {LOCATION_OPTIONS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Found by</label>
            <input
              className="w-full px-3 py-2 text-sm border rounded-md"
              value={foundBy}
              onChange={(e) => setFoundBy(e.target.value)}
            />
          </div>
          {/* --- NEW: Photo Upload Section --- */}
          <div className="space-y-2">
            <label className="block text-sm font-medium">
              Item Photo (Optional)
            </label>
            <div className="flex items-center">
              <input
                id="editPhotoUpload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('editPhotoUpload')?.click()}
              >
                <Camera className="mr-2" size={16} />
                {photoFile ? "Change File" : photoPreview ? "Replace Photo" : "Upload Photo"}
              </Button>
              <span className="ml-3 text-sm text-muted-foreground">
                {photoFile ? photoFile.name : photoPreview ? "Photo added" : "No file chosen"}
              </span>
            </div>
            {photoPreview && (
              <div className="mt-2 border rounded-md p-2 max-w-[200px]">
                <img
                  src={photoPreview.startsWith('data:') ? photoPreview : signedPhotoUrl || ''}
                  alt="Preview"
                  className="max-h-[150px] mx-auto object-contain rounded"
                />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isUploading}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditLostItemModal;
